import React, { useEffect, useState } from 'react';
import { Checkbox, FormControlLabel, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

import {
  getDefaultTrussDesignDrawingTemplateIds,
  hasTrussDesignDrawingTemplateChoices,
  TrussDesignDrawingTemplates,
} from '@paragon/client-tools';
import { DEFAULT_TRUSS_DESIGN_DRAWING_OPTIONS, TrussDesignDrawingOptions } from '@paragon/contract-library';

import { ChooseTrussDesignDrawingTemplates } from './ChooseTrussDesignDrawingTemplates';

const withDefaultTemplateIds = (options: TrussDesignDrawingOptions, templates: TrussDesignDrawingTemplates) => {
  const defaultTemplateIds = getDefaultTrussDesignDrawingTemplateIds(templates, options.useRepairTemplate);
  return {
    ...options,
    templateId: options.templateId ?? defaultTemplateIds.templateId,
    overflowTemplateId: options.overflowTemplateId ?? defaultTemplateIds.overflowTemplateId,
  };
};

/**
 * The templates may still be loading, so the default template IDs are filled in whenever they arrive.
 */
export function useTrussDesignDrawingOptions(
  templates: TrussDesignDrawingTemplates,
  initialOptions?: Partial<TrussDesignDrawingOptions>,
) {
  const [options, setOptions] = useState<TrussDesignDrawingOptions>(() =>
    withDefaultTemplateIds({ ...DEFAULT_TRUSS_DESIGN_DRAWING_OPTIONS, ...initialOptions }, templates),
  );
  useEffect(() => setOptions((oldOptions) => withDefaultTemplateIds(oldOptions, templates)), [templates]);
  return [options, setOptions] as const;
}

/**
 * Repair drawings never link to their Shared Component Set, so a snapshot shared with it would be unreachable, and they
 * are never dark.
 */
export const getTrussDesignDrawingOptionsToSend = (options: TrussDesignDrawingOptions): TrussDesignDrawingOptions =>
  options.useRepairTemplate ? { ...options, shareLayout: false, forceDarkMode: false } : options;

interface TrussDesignDrawingOptionsFieldsProps {
  readonly options: TrussDesignDrawingOptions;
  readonly onChange: (options: TrussDesignDrawingOptions) => void;
  readonly templates: TrussDesignDrawingTemplates;
  readonly showFileType: boolean;
  readonly showForceDarkMode: boolean;
  readonly showIgnoreErrors: boolean;
  readonly showShareLayout: boolean;
}

export function TrussDesignDrawingOptionsFields({
  options,
  onChange,
  templates,
  showFileType,
  showForceDarkMode,
  showIgnoreErrors,
  showShareLayout,
}: TrussDesignDrawingOptionsFieldsProps) {
  const set = (changes: Partial<TrussDesignDrawingOptions>) => onChange({ ...options, ...changes });

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
        <Typography sx={{ minWidth: 80 }}>Format:</Typography>
        <ToggleButtonGroup
          value={options.useRepairTemplate ? 'repair' : 'standard'}
          size="small"
          exclusive
          onChange={(_, value: 'standard' | 'repair' | null) => {
            if (!value) return;
            const useRepairTemplate = value === 'repair';
            set({
              useRepairTemplate,
              templateId: getDefaultTrussDesignDrawingTemplateIds(templates, useRepairTemplate).templateId,
            });
          }}
        >
          <ToggleButton value="standard">Standard</ToggleButton>
          <ToggleButton value="repair">Repair</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      {showFileType && (
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
          <Typography sx={{ minWidth: 80 }}>File Type:</Typography>
          <ToggleButtonGroup
            value={options.mergeIntoOnePdf ? 'merged' : 'separate'}
            size="small"
            exclusive
            onChange={(_, value: 'separate' | 'merged' | null) => {
              if (value) set({ mergeIntoOnePdf: value === 'merged' });
            }}
          >
            <ToggleButton value="separate">Separate PDFs</ToggleButton>
            <ToggleButton value="merged">Merged PDF</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      )}
      {showForceDarkMode && !options.useRepairTemplate && (
        <FormControlLabel
          label="Force Dark Mode"
          control={
            <Checkbox checked={options.forceDarkMode} onChange={(_, checked) => set({ forceDarkMode: checked })} />
          }
        />
      )}
      {showIgnoreErrors && !options.useRepairTemplate && (
        <FormControlLabel
          label="Do not include analysis warning text"
          control={
            <Checkbox checked={options.ignoreErrors} onChange={(_, checked) => set({ ignoreErrors: checked })} />
          }
        />
      )}
      {showShareLayout && !options.useRepairTemplate && (
        <FormControlLabel
          label="Share Project Snapshot (Hierarchy, Layout)"
          control={<Checkbox checked={options.shareLayout} onChange={(_, checked) => set({ shareLayout: checked })} />}
        />
      )}
      {hasTrussDesignDrawingTemplateChoices(templates, options.useRepairTemplate) && (
        <ChooseTrussDesignDrawingTemplates
          templates={templates}
          selectedTrussDesignDrawingTemplateId={options.templateId}
          setSelectedTrussDesignDrawingTemplateId={(templateId) => set({ templateId })}
          selectedTrussDesignDrawingOverflowTemplateId={options.overflowTemplateId}
          setSelectedTrussDesignDrawingOverflowTemplateId={(overflowTemplateId) => set({ overflowTemplateId })}
          type={options.useRepairTemplate ? 'repair' : 'standard'}
        />
      )}
    </Stack>
  );
}
